/**
 * Dev-only API-key login, mirroring web-v2 `src/lib/e2e-auth.ts`: open any
 * page with `?e2eAuthToken=sk_…` and requests carry it as a Bearer token.
 * `?e2eAuthToken=clear` forgets it.
 *
 * Auto-login: set `VITE_DEV_AUTO_LOGIN_TOKEN=sk_…` in `.env.local` and every
 * page is signed in with that key — for when Google sign-in isn't registered
 * for localhost. The key is a team's, not a person's (no seat, not staff), so
 * anything that asks who you are refuses it; the dev accounts
 * (`lib/dev-accounts.ts`) are real people. Signing out, or signing in as
 * anyone, pauses it in this browser (every tab: a real session is never
 * overridden by the key) until the sign-in page's "team key" button resumes it.
 *
 * All of it is compiled out of production builds (`import.meta.env.PROD`).
 */
const STORAGE_KEY = 'dreamwell:e2e-auth-token';
const PARAM = 'e2eAuthToken';
const AUTO_PAUSED_KEY = 'dreamwell:dev-auto-login-paused';

function autoLoginToken(): string | null {
  if (import.meta.env.PROD) return null;
  // Never through a tunnel: anyone with the link would be signed in as the key's owner.
  if (!/^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname)) return null;
  const token = normalize(import.meta.env.VITE_DEV_AUTO_LOGIN_TOKEN);
  if (!token) return null;
  try {
    if (window.localStorage.getItem(AUTO_PAUSED_KEY)) return null;
  } catch {
    /* storage blocked: auto-login stays on */
  }
  return token;
}

function normalize(value: string | null | undefined): string | null {
  const token = value?.trim();
  return token && token.startsWith('sk_') ? token : null;
}

/** `href` without the dev-login parameter, so a forgotten key isn't picked up again from the address. */
export function withoutDevAuthParam(href: string): string {
  const url = new URL(href, window.location.origin);
  if (!url.searchParams.has(PARAM)) return href;
  url.searchParams.delete(PARAM);
  // An absolute address stays absolute; a path (the router's `location.href`) stays a path.
  return /^[a-z][a-z\d+.-]*:/i.test(href) ? url.toString() : `${url.pathname}${url.search}${url.hash}`;
}

export function clearDevAuthToken(): void {
  try {
    // Out of the address too: `getDevAuthToken` reads the parameter first and would store the key again.
    const here = window.location.href;
    const stripped = withoutDevAuthParam(here);
    if (stripped !== here) window.history.replaceState(window.history.state, '', stripped);
    window.localStorage.removeItem(STORAGE_KEY);
    pauseDevAutoLogin();
  } catch {
    /* storage blocked */
  }
}

/** Stop sending the dev key in this browser (a real person signed in, or out); `resumeDevAutoLogin` sends it again. */
export function pauseDevAutoLogin(): void {
  if (import.meta.env.PROD || !import.meta.env.VITE_DEV_AUTO_LOGIN_TOKEN) return;
  try {
    window.localStorage.setItem(AUTO_PAUSED_KEY, '1');
  } catch {
    /* storage blocked */
  }
}

/** Turn auto-login back on in this browser (the sign-in page offers it). */
export function resumeDevAutoLogin(): boolean {
  if (import.meta.env.PROD || !import.meta.env.VITE_DEV_AUTO_LOGIN_TOKEN) return false;
  try {
    window.localStorage.removeItem(AUTO_PAUSED_KEY);
  } catch {
    /* storage blocked */
  }
  return true;
}

export const devAutoLoginAvailable = (): boolean => !import.meta.env.PROD && Boolean(normalize(import.meta.env.VITE_DEV_AUTO_LOGIN_TOKEN));

export function getDevAuthToken(): string | null {
  if (import.meta.env.PROD || typeof window === 'undefined') return null;
  try {
    const param = new URLSearchParams(window.location.search).get(PARAM);
    if (param === 'clear') {
      clearDevAuthToken();
      return null;
    }
    const fromUrl = normalize(param);
    if (fromUrl) {
      window.localStorage.setItem(STORAGE_KEY, fromUrl);
      return fromUrl;
    }
    return normalize(window.localStorage.getItem(STORAGE_KEY)) ?? autoLoginToken();
  } catch {
    return autoLoginToken();
  }
}
